import { MarketingLayout } from './MarketingLayout'


function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 48 }}>
      <h2 style={{
        fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700,
        color: 'var(--navy-700)', marginBottom: 16,
        paddingBottom: 12, borderBottom: '1px solid var(--border-subtle)',
      }}>{title}</h2>
      {children}
    </section>
  )
}

function P({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontSize: 14, color: 'var(--fg-2)', lineHeight: 1.85, marginBottom: 12 }}>
      {children}
    </p>
  )
}

function UL({ items }: { items: string[] }) {
  return (
    <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
      {items.map((item, i) => (
        <li key={i} style={{ fontSize: 14, color: 'var(--fg-2)', lineHeight: 1.85, marginBottom: 6 }}>{item}</li>
      ))}
    </ul>
  )
}

function H3({ children }: { children: React.ReactNode }) {
  return (
    <h3 style={{
      fontSize: 15, fontWeight: 700, color: 'var(--navy-700)', marginBottom: 8, marginTop: 20,
    }}>{children}</h3>
  )
}

export function PrivacyPage() {

  return (
    <MarketingLayout>
      <section style={{
        background: 'var(--navy-900, #0d1b2e)',
        padding: '72px var(--space-6) 56px',
      }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 4vw, 42px)',
            fontWeight: 700, color: '#fff', letterSpacing: '-0.03em', marginBottom: 12,
          }}>Privacy Policy</h1>
          <p style={{ fontSize: 14, color: 'var(--neutral-400)' }}>
            AutoAxxiz by JVS Ethics Lab · Last updated: 1 April 2026 · POPIA-oriented privacy practices
          </p>
        </div>
      </section>

      <div style={{ padding: '72px var(--space-6)', background: '#fff' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>

          <div style={{
            background: 'rgba(26,46,74,0.04)', border: '1px solid var(--border-subtle)',
            borderRadius: 10, padding: '16px 20px', marginBottom: 48,
          }}>
            <p style={{ fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.7, margin: 0 }}>
              <strong>In plain language:</strong> AutoAxxiz is a product by JVS Ethics Lab. We collect the minimum information needed to run your workshop software. We never sell your data. You have POPIA rights including access, correction, and deletion. Contact privacy@autoaxxiz.co.za for any privacy matter.
            </p>
          </div>

          <Section title="1. Who we are and our role under POPIA">
            <P>JVS Ethics Lab ("JVS Ethics Lab", "AutoAxxiz", "we", "us") owns and operates AutoAxxiz, the workshop management platform.</P>
            <P>Under the Protection of Personal Information Act, 2013 (POPIA), we act as:</P>
            <UL items={[
              'Responsible party for personal information we collect directly from you (your account, billing, and support interactions).',
              'Operator for personal information you store about your customers and employees inside the platform (we process this under your instruction).',
            ]} />
            <P>Our Information Officer is responsible for POPIA compliance. Contact: <strong>privacy@autoaxxiz.co.za</strong></P>
          </Section>

          <Section title="2. Personal information we collect">
            <H3>2.1 Account and subscriber information</H3>
            <UL items={[
              'Full name, email address, mobile number (for account holders and authorised users)',
              'Workshop name, physical address, VAT number',
              'Billing information for JVS Ethics Lab invoices (name on account, payment method details — card numbers handled by PCI-compliant processors, not stored by us)',
              'IP address and browser/device information for security logging',
            ]} />

            <H3>2.2 Customer and vehicle data you store</H3>
            <P>When you use AutoAxxiz to manage your workshop, you store personal information about your own customers. This includes their names, phone numbers, email addresses, and vehicle details. AutoAxxiz processes this data as an operator on your behalf — we do not use it for any purpose other than providing the Service to you.</P>

            <H3>2.3 Usage and technical data</H3>
            <UL items={[
              'Log data (API requests, timestamps, errors) for security and debugging',
              'Feature usage patterns (aggregated, not individual) to improve the product',
              'WhatsApp message delivery status (not message content beyond what you explicitly send)',
            ]} />

            <H3>2.4 WhatsApp message content</H3>
            <P>Messages you send to your customers via the WhatsApp Business API are transmitted through Meta's infrastructure. The content of customer WhatsApp conversations is subject to Meta's own privacy policy. We store structured message metadata (type, timestamp, delivery status, linked job card) but not raw message content beyond what you type into AutoAxxiz.</P>
          </Section>

          <Section title="3. Why we process personal information">
            <P>We process personal information for the following lawful bases under POPIA:</P>
            <UL items={[
              'Contractual necessity: to provide, operate, and support the AutoAxxiz service you subscribe to.',
              'Legal obligation: to issue SARS-compliant tax invoices, retain records as required by the Companies Act and VAT Act, and respond to lawful information requests.',
              'Legitimate interest: to send transactional emails and product updates relevant to your account, maintain security logs, and improve service quality.',
              'Consent: where required for marketing communications beyond transactional messages (you can withdraw at any time).',
            ]} />
          </Section>

          <Section title="4. Where your data is stored">
            <P>The production architecture is designed for South African data residency and secure object storage for workshop media. The final hosting provider and region are confirmed during production infrastructure setup.</P>
            <P>External services may process information outside South Africa. Confirm the active providers and their processing locations during onboarding, including:</P>
            <UL items={[
              "WhatsApp message content that passes through Meta's global infrastructure — governed by Meta's Data Processing Terms.",
              'Billing information processed by JVS Ethics Lab payment providers.',
              'Aggregated, anonymised usage analytics processed by our analytics provider.',
            ]} />
          </Section>

          <Section title="5. Sharing personal information">
            <P>We do not sell personal information. We share personal information only with:</P>
            <UL items={[
              'Meta Platforms (WhatsApp Business API — to send and receive WhatsApp messages on your behalf)',
              'Amazon Web Services (infrastructure and hosting)',
              'PayFast / payment processors (billing and payment processing)',
              'Xero / Sage (only the invoice and customer data you explicitly sync, on your instruction)',
              'Regulatory and law enforcement authorities when required by South African law',
            ]} />
            <P>All processors are bound by data processing agreements. We do not share personal information with advertisers or data brokers.</P>
          </Section>

          <Section title="6. Retention">
            <P>We retain personal information for as long as your account is active and for 3 years after account closure for legal and accounting purposes, unless a shorter period is required by law or requested by you.</P>
            <P>Contact support to request deletion after account closure. We will confirm the scope, timing and backup retention for your deployment; records subject to legal retention obligations may need to be retained.</P>
          </Section>

          <Section title="7. Your rights under POPIA">
            <P>As a data subject under POPIA, you have the right to:</P>
            <UL items={[
              'Access: request a copy of personal information we hold about you.',
              'Correction: request correction of inaccurate personal information.',
              'Deletion: request deletion of personal information (subject to legal retention obligations).',
              'Objection: object to processing based on legitimate interests.',
              'Complaint: lodge a complaint with the Information Regulator of South Africa (inforeg.org.za).',
              'Restriction: request we restrict processing in certain circumstances.',
            ]} />
            <P>To exercise any of these rights, email <strong>privacy@autoaxxiz.co.za</strong>. We will respond within 30 days.</P>
          </Section>

          <Section title="8. Security">
            <P>We implement security measures appropriate to the risk, including:</P>
            <UL items={[
              'TLS encryption for service connections',
              'AES-256-GCM encryption for selected sensitive customer fields',
              'Bcrypt password hashing — we never store plain-text passwords',
              'Role-based access controls with short-lived access tokens and revocable rotating refresh tokens',
              'Regular security reviews and vulnerability scanning',
              'AWS VPC network isolation and least-privilege IAM policies',
            ]} />
            <P>No system is completely secure. In the event of a breach affecting your personal information, we will notify you and the Information Regulator as required by POPIA within 72 hours of becoming aware of the breach.</P>
          </Section>

          <Section title="9. Cookies and tracking">
            <P>The AutoAxxiz web application uses:</P>
            <UL items={[
              'Session cookies (strictly necessary): to maintain your login state. These cannot be disabled without breaking core functionality.',
              'Google Analytics cookies (optional): after you choose Allow analytics on the marketing website, Google receives page visits, CTA clicks and successful enquiry events. You can decline or withdraw consent using Analytics preferences in the footer. Google may process this data outside South Africa.',
            ]} />
            <P>The marketing Google Analytics integration disables advertising signals and does not intentionally send names, phone numbers, email addresses, form contents or URL query strings. It is restricted to public marketing pages; staging and workshop screens are excluded. Limited first-party event counts are also collected without analytics cookies and honour browser Do Not Track.</P>
          </Section>

          <Section title="10. Children">
            <P>The Service is intended for business use by adults. We do not knowingly collect personal information from individuals under 18. If you believe a minor's data has been submitted, contact privacy@autoaxxiz.co.za immediately.</P>
          </Section>

          <Section title="11. Changes to this policy">
            <P>We may update this Privacy Policy. We will notify you by email and in-app notification at least 30 days before material changes take effect. The date at the top of this page shows when it was last updated.</P>
          </Section>

          <Section title="12. Contact">
            <P>
              <strong>Information Officer</strong><br />
              AutoAxxiz (Pty) Ltd<br />
              Johannesburg, Gauteng, South Africa<br />
              Email: privacy@autoaxxiz.co.za
            </P>
            <P>
              <strong>Information Regulator of South Africa</strong><br />
              inforeg.org.za · complaints.IR@justice.gov.za
            </P>
          </Section>

        </div>
      </div>
    </MarketingLayout>
  )
}
